import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { productService, categoryService, quoteService } from '../services/api';
import { blogService } from '../services/blogService';
import ExportButton from '../components/ExportButton';
import SmartImage from '../components/SmartImage';
import './Dashboard.css';
import { Typography, Row, Col, Card, Space, Button, Tag, List } from 'antd';
import { Color } from 'antd/es/color-picker';
const { Title, Text } = Typography;

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    products: { total: 0, published: 0, draft: 0 },
    categories: { total: 0, active: 0 },
    quotes: { total: 0, pending: 0, responded: 0 }
  });
  const [recentProducts, setRecentProducts] = useState([]);
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      
      // Charger les statistiques des produits
      const productsResponse = await productService.getProducts({ limit: 1 });
      const publishedProducts = await productService.getProducts({ 
        limit: 1, 
        status: 'published' 
      });
      const draftProducts = await productService.getProducts({ 
        limit: 1, 
        status: 'draft' 
      });

      // Charger les statistiques des catégories
      const categoriesResponse = await categoryService.getCategoryStats();

      // Charger les statistiques des devis
      const quotesResponse = await quoteService.getQuoteStats();

      // Charger les produits récents
      const recentProductsResponse = await productService.getProducts({ 
        limit: 5, 
        sortBy: 'createdAt', 
        sortOrder: 'desc' 
      });

      // Charger les blogs récents
      const recentBlogsResponse = await blogService.list({ 
        limit: 5, 
        sortBy: 'createdAt', 
        sortOrder: 'desc' 
      });

      setStats({
        products: {
          total: productsResponse.pagination.totalItems,
          published: publishedProducts.pagination.totalItems,
          draft: draftProducts.pagination.totalItems
        },
        categories: {
          total: categoriesResponse.totalCategories,
          active: categoriesResponse.activeCategories
        },
        quotes: {
          total: quotesResponse.total,
          pending: quotesResponse.byStatus.pending || 0,
          responded: (quotesResponse.byStatus.quoted || 0) + (quotesResponse.byStatus.accepted || 0)
        }
      });

      setRecentProducts(recentProductsResponse.products);
      setRecentBlogs(recentBlogsResponse.blogs || recentBlogsResponse.items || recentBlogsResponse.data || []);

    } catch (error) {
      console.error('Erreur lors du chargement du tableau de bord:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, color, link }) => (
    <Col xs={24} sm={12} md={8}>
      <Link to={link}>
        <Card hoverable>
          <Space align="center" size={16}>
            <Tag color={color} style={{ padding: '4px 8px' }}>
              <i className={icon}></i>
            </Tag>
            <div>
              <Text type="secondary">{title}</Text>
              <Title level={3} style={{ margin: 0 }}>{value}</Title>
            </div>
          </Space>
        </Card>
      </Link>
    </Col>
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="dashboard">
        <div className="dashboard__loading">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard__header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="dashboard__welcome">
          <Title level={3} style={{ marginBottom: 4 }}>Bienvenue, {user?.username} !</Title>
          <Text type="secondary">Voici un aperçu de votre espace d'administration DOMOELEC</Text>
        </div>
        <Space>
          <Button type="primary" style={{backgroundColor:'#002c3e'}}>
            <Link to="/admin/products/new"><i className="fas fa-plus" ></i> Nouveau produit</Link>
          </Button>
        </Space>
      </div>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <StatCard title="Produits" value={stats.products.total} icon="fas fa-box" color="blue" link="/admin/products" />
        <StatCard title="Catégories" value={stats.categories.total} icon="fas fa-tags" color="green" link="/admin/categories" />
        <StatCard title="Produits publiés" value={stats.products.published} icon="fas fa-eye" color="purple" link="/admin/products?status=published" />
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} md={12}>
          <Card title="Produits récents" extra={<Link to="/admin/products">Voir tous</Link>}>
            {recentProducts.length > 0 ? (
              <List
                itemLayout="horizontal"
                dataSource={recentProducts}
                renderItem={(product) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={
                        <SmartImage
                          src={product.images?.main}
                          alt={product.name}
                          width={56}
                          height={56}
                          imgStyle={{ borderRadius: 8 }}
                        />
                      }
                      title={<span>{product.name} <Tag style={{ marginLeft: 8 }}>{product.reference}</Tag></span>}
                      description={<Text type="secondary">{formatDate(product.createdAt)}</Text>}
                    />
                    <Tag color={product.status === 'published' ? 'green' : product.status === 'draft' ? 'orange' : 'default'}>
                      {product.status === 'published' ? 'Publié' : product.status === 'draft' ? 'Brouillon' : 'Archivé'}
                    </Tag>
                  </List.Item>
                )}
              />
            ) : (
              <Text type="secondary">Aucun produit trouvé</Text>
            )}
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="Blogs récents" extra={<Link to="/admin/blogs">Voir tous</Link>}>
            {recentBlogs.length > 0 ? (
              <List
                itemLayout="horizontal"
                dataSource={recentBlogs}
                renderItem={(blog) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<i className="fas fa-newspaper"></i>}
                      title={<span>{blog.title} {blog.status ? <Tag style={{ marginLeft: 8 }}>{blog.status}</Tag> : null}</span>}
                      description={<Text type="secondary">{formatDate(blog.createdAt)}</Text>}
                    />
                    {blog.isPublished !== undefined && (
                      <Tag color={blog.isPublished ? 'green' : 'orange'}>
                        {blog.isPublished ? 'Publié' : 'Brouillon'}
                      </Tag>
                    )}
                  </List.Item>
                )}
              />
            ) : (
              <Text type="secondary">Aucun article</Text>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
